import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import { 
  Plus, 
  Search, 
  Users,
  MoreHorizontal,
  Mail,
  Phone,
  Building2,
  Eye,
  Trash2,
  Pencil,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  Euro,
  TrendingUp,
  Loader2,
  Package,
  UserCircle,
  X,
  Filter,
  Download,
  CheckCircle,
  XCircle,
  SlidersHorizontal,
  FileSpreadsheet
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/axios';

const ClientsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsRevenue, setClientsRevenue] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('table');
  // Filters
  const [filterByManager, setFilterByManager] = useState('all');
  const [filterByStatus, setFilterByStatus] = useState('all'); // all, active, inactive
  const [filterByDelivery, setFilterByDelivery] = useState('all'); // all, delivered, never_delivered
  const [filterByProjects, setFilterByProjects] = useState('all'); // all, with_projects, no_projects
  const [showFilters, setShowFilters] = useState(true);
  const [newClient, setNewClient] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    phone: ''
  });

  // Extract unique account managers from clients
  const accountManagers = useMemo(() => {
    const managers = {};
    clients.forEach(client => {
      if (client.account_manager_id && client.account_manager_name) {
        managers[client.account_manager_id] = client.account_manager_name;
      }
    });
    return Object.entries(managers).map(([id, name]) => ({ id, name }));
  }, [clients]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filterByManager !== 'all') count++;
    if (filterByStatus !== 'all') count++;
    if (filterByDelivery !== 'all') count++;
    if (filterByProjects !== 'all') count++;
    return count;
  }, [filterByManager, filterByStatus, filterByDelivery, filterByProjects]);

  // Reset all filters
  const resetFilters = () => {
    setFilterByManager('all');
    setFilterByStatus('all');
    setFilterByDelivery('all');
    setFilterByProjects('all');
    setSearchQuery('');
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setShowCreateModal(true);
    }
  }, [searchParams]);

  const fetchClients = async () => {
    try {
      // Use enriched endpoint to get clients with account_manager_name and last_delivery_date
      const response = await api.get('/users/clients-enriched');
      setClients(response.data);
      
      // Fetch revenue data for each client
      fetchClientsRevenue(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsRevenue = async (clientsList) => {
    setLoadingRevenue(true);
    try {
      // Fetch revenue data for all clients
      const revenueData = {};
      const currentYear = new Date().getFullYear();
      
      // Batch fetch - get all projects and invoices
      const [projectsRes, invoicesRes] = await Promise.all([
        api.get('/projects'),
        api.get('/invoices')
      ]);
      
      const projects = projectsRes.data || [];
      const invoices = invoicesRes.data || [];
      
      // Calculate revenue per client
      clientsList.forEach(client => {
        const clientProjects = projects.filter(p => p.client_id === client.id);
        const clientInvoices = invoices.filter(inv => {
          const project = projects.find(p => p.id === inv.project_id);
          return project?.client_id === client.id;
        });
        
        // CA Total (from paid invoices)
        const caTotal = clientInvoices
          .filter(inv => inv.status === 'paid')
          .reduce((sum, inv) => sum + (inv.total_ttc || inv.amount || 0), 0);
        
        // CA Year (from paid invoices this year)
        const caYear = clientInvoices
          .filter(inv => {
            if (inv.status !== 'paid') return false;
            const invDate = new Date(inv.paid_at || inv.created_at);
            return invDate.getFullYear() === currentYear;
          })
          .reduce((sum, inv) => sum + (inv.total_ttc || inv.amount || 0), 0);
        
        // If no invoices, try to calculate from project budgets
        let caTotalEstimate = caTotal;
        let caYearEstimate = caYear;
        
        if (caTotal === 0) {
          caTotalEstimate = clientProjects
            .filter(p => p.status === 'completed' || p.status === 'delivered')
            .reduce((sum, p) => sum + (p.budget || p.total_budget || 0), 0);
        }
        
        if (caYear === 0) {
          caYearEstimate = clientProjects
            .filter(p => {
              if (p.status !== 'completed' && p.status !== 'delivered') return false;
              const pDate = new Date(p.completed_at || p.updated_at || p.created_at);
              return pDate.getFullYear() === currentYear;
            })
            .reduce((sum, p) => sum + (p.budget || p.total_budget || 0), 0);
        }
        
        revenueData[client.id] = {
          ca_total: caTotalEstimate,
          ca_year: caYearEstimate,
          projects_count: clientProjects.length
        };
      });
      
      setClientsRevenue(revenueData);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    } finally {
      setLoadingRevenue(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClient.email) {
      toast.error('L\'email est obligatoire');
      return;
    }
    setCreating(true);
    try {
      const response = await api.post('/users/client', newClient);
      toast.success(`Client créé ! Mot de passe temporaire: ${response.data.temp_password}`);
      setShowCreateModal(false);
      setNewClient({ email: '', first_name: '', last_name: '', company_name: '', phone: '' });
      fetchClients();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClient = async (clientId, clientName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${clientName} ?\n\nAttention: Cette action supprimera également tous les projets associés à ce client. Cette action est irréversible.`)) return;
    
    try {
      await api.delete(`/users/${clientId}`);
      toast.success('Client supprimé avec succès');
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30" />;
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1 text-emerald-600" /> 
      : <ArrowDown className="w-4 h-4 ml-1 text-emerald-600" />;
  };

  // Filter and sort clients
  const sortedClients = useMemo(() => {
    let filtered = clients.filter(c => {
      // Search filter
      const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
      const company = (c.company_name || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      const query = searchQuery.toLowerCase();
      const matchesSearch = fullName.includes(query) || company.includes(query) || email.includes(query);
      
      // Manager filter
      const matchesManager = filterByManager === 'all' 
        || (filterByManager === 'unassigned' && !c.account_manager_id)
        || c.account_manager_id === filterByManager;
      
      // Status filter
      const isActive = c.is_active !== false;
      const matchesStatus = filterByStatus === 'all'
        || (filterByStatus === 'active' && isActive)
        || (filterByStatus === 'inactive' && !isActive);
      
      // Delivery filter
      const hasDelivered = !!c.last_delivery_date;
      const matchesDelivery = filterByDelivery === 'all'
        || (filterByDelivery === 'delivered' && hasDelivered)
        || (filterByDelivery === 'never_delivered' && !hasDelivered);
      
      // Projects filter (need revenue data)
      const projectsCount = clientsRevenue[c.id]?.projects_count || 0;
      const matchesProjects = filterByProjects === 'all'
        || (filterByProjects === 'with_projects' && projectsCount > 0)
        || (filterByProjects === 'no_projects' && projectsCount === 0);
      
      return matchesSearch && matchesManager && matchesStatus && matchesDelivery && matchesProjects;
    });

    return filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
          aVal = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
          bVal = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
          break;
        case 'company':
          aVal = (a.company_name || '').toLowerCase();
          bVal = (b.company_name || '').toLowerCase();
          break;
        case 'created_at':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        case 'ca_year':
          aVal = clientsRevenue[a.id]?.ca_year || 0;
          bVal = clientsRevenue[b.id]?.ca_year || 0;
          break;
        case 'ca_total':
          aVal = clientsRevenue[a.id]?.ca_total || 0;
          bVal = clientsRevenue[b.id]?.ca_total || 0;
          break;
        case 'last_delivery':
          aVal = a.last_delivery_date ? new Date(a.last_delivery_date).getTime() : 0;
          bVal = b.last_delivery_date ? new Date(b.last_delivery_date).getTime() : 0;
          break;
        case 'account_manager':
          aVal = (a.account_manager_name || '').toLowerCase();
          bVal = (b.account_manager_name || '').toLowerCase();
          break;
        default:
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [clients, searchQuery, sortBy, sortOrder, clientsRevenue, filterByManager, filterByStatus, filterByDelivery, filterByProjects]);

  // Export to CSV function
  const exportToCSV = () => {
    // Define CSV headers
    const headers = [
      'Prénom',
      'Nom',
      'Email',
      'Téléphone',
      'Société',
      'Statut',
      'Date création',
      'Responsable compte',
      'Dernière livraison',
      'CA Année',
      'CA Total',
      'Nb Projets'
    ];
    
    // Build CSV rows from filtered clients
    const rows = sortedClients.map(client => {
      const revenue = clientsRevenue[client.id] || {};
      return [
        client.first_name || '',
        client.last_name || '',
        client.email || '',
        client.phone || '',
        client.company_name || '',
        client.is_active !== false ? 'Actif' : 'Inactif',
        client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR') : '',
        client.account_manager_name || '',
        client.last_delivery_date ? new Date(client.last_delivery_date).toLocaleDateString('fr-FR') : '',
        revenue.ca_year || 0,
        revenue.ca_total || 0,
        revenue.projects_count || 0
      ];
    });
    
    // Create CSV content
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => {
        // Escape quotes and wrap in quotes if contains separator or quotes
        const cellStr = String(cell).replace(/"/g, '""');
        return cellStr.includes(';') || cellStr.includes('"') || cellStr.includes('\n') 
          ? `"${cellStr}"` 
          : cellStr;
      }).join(';'))
    ].join('\n');
    
    // Add BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().slice(0, 10);
    const filterLabel = activeFiltersCount > 0 ? '_filtre' : '';
    link.setAttribute('href', url);
    link.setAttribute('download', `clients_export${filterLabel}_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${sortedClients.length} client(s) exporté(s) en CSV`);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '0€';
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}k€`;
    }
    return `${Math.round(amount)}€`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short',
      year: '2-digit'
    });
  };

  // Calculate totals
  const totalCAYear = useMemo(() => {
    return Object.values(clientsRevenue).reduce((sum, r) => sum + (r?.ca_year || 0), 0);
  }, [clientsRevenue]);

  const totalCATotal = useMemo(() => {
    return Object.values(clientsRevenue).reduce((sum, r) => sum + (r?.ca_total || 0), 0);
  }, [clientsRevenue]);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8 pt-32",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Clients
            </h1>
            <p className="text-[#64748b] mt-1">
              {sortedClients.length} client(s) {activeFiltersCount > 0 ? 'filtrés' : 'enregistrés'}
            </p>
          </div>
          
          <div className="flex gap-2">
            {/* Export CSV Button */}
            <Button 
              variant="outline"
              onClick={exportToCSV}
              disabled={sortedClients.length === 0}
              data-testid="export-csv-btn"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exporter CSV
              {sortedClients.length > 0 && (
                <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">
                  {sortedClients.length}
                </Badge>
              )}
            </Button>
            
            <Button 
              onClick={() => setShowCreateModal(true)}
              style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
              className="font-bold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau client
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">CA Année {new Date().getFullYear()}</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {loadingRevenue ? <Loader2 className="w-5 h-5 animate-spin" /> : formatCurrency(totalCAYear)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">CA Total (depuis 2026)</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {loadingRevenue ? <Loader2 className="w-5 h-5 animate-spin" /> : formatCurrency(totalCATotal)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Euro className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Nombre de clients</p>
                  <p className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                    {clients.length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search, Filter and Sort */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            {/* Main row: Search + Filter toggle + Sort */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                <Input
                  placeholder="Rechercher un client..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-default pl-10"
                  data-testid="search-clients-input"
                />
              </div>
              
              {/* Toggle filters button */}
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  "flex items-center gap-2",
                  activeFiltersCount > 0 && "border-emerald-300 bg-emerald-50"
                )}
                data-testid="toggle-filters-btn"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filtres
                {activeFiltersCount > 0 && (
                  <Badge className="bg-emerald-600 text-white">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
              
              <div className="flex gap-2">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Trier par..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">
                      <span className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Ancienneté
                      </span>
                    </SelectItem>
                    <SelectItem value="ca_year">
                      <span className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        CA Année
                      </span>
                    </SelectItem>
                    <SelectItem value="ca_total">
                      <span className="flex items-center gap-2">
                        <Euro className="w-4 h-4" />
                        CA Total
                      </span>
                    </SelectItem>
                    <SelectItem value="last_delivery">
                      <span className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Dernière livraison
                      </span>
                    </SelectItem>
                    <SelectItem value="account_manager">
                      <span className="flex items-center gap-2">
                        <UserCircle className="w-4 h-4" />
                        Responsable
                      </span>
                    </SelectItem>
                    <SelectItem value="name">
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Nom
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
                >
                  {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            {/* Expandable filters panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Filter by Account Manager */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Responsable</label>
                    <Select value={filterByManager} onValueChange={setFilterByManager}>
                      <SelectTrigger data-testid="filter-by-manager">
                        <UserCircle className="w-4 h-4 mr-2 text-slate-400" />
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les responsables</SelectItem>
                        <SelectItem value="unassigned">
                          <span className="text-slate-500">Sans responsable</span>
                        </SelectItem>
                        {accountManagers.map(manager => (
                          <SelectItem key={manager.id} value={manager.id}>
                            {manager.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Filter by Status */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Statut</label>
                    <Select value={filterByStatus} onValueChange={setFilterByStatus}>
                      <SelectTrigger data-testid="filter-by-status">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les statuts</SelectItem>
                        <SelectItem value="active">
                          <span className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Actifs
                          </span>
                        </SelectItem>
                        <SelectItem value="inactive">
                          <span className="flex items-center gap-2">
                            <XCircle className="w-4 h-4 text-slate-400" />
                            Inactifs
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Filter by Delivery */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Livraison</label>
                    <Select value={filterByDelivery} onValueChange={setFilterByDelivery}>
                      <SelectTrigger data-testid="filter-by-delivery">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="delivered">
                          <span className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-emerald-500" />
                            Avec livraison
                          </span>
                        </SelectItem>
                        <SelectItem value="never_delivered">
                          <span className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-slate-400" />
                            Sans livraison
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Filter by Projects */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Projets</label>
                    <Select value={filterByProjects} onValueChange={setFilterByProjects}>
                      <SelectTrigger data-testid="filter-by-projects">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="with_projects">
                          <span className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-blue-500" />
                            Avec projets
                          </span>
                        </SelectItem>
                        <SelectItem value="no_projects">
                          <span className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            Sans projet
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Reset filters button */}
                {activeFiltersCount > 0 && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetFilters}
                      className="text-slate-500 hover:text-slate-700"
                      data-testid="reset-filters-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Réinitialiser les filtres
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Active filters summary (shown when filters panel is closed) */}
            {!showFilters && activeFiltersCount > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="flex flex-wrap items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Filtres actifs :</span>
                  
                  {filterByManager !== 'all' && (
                    <Badge 
                      variant="secondary" 
                      className="flex items-center gap-1 bg-emerald-50 text-emerald-700"
                    >
                      <UserCircle className="w-3 h-3" />
                      {filterByManager === 'unassigned' 
                        ? 'Sans responsable' 
                        : accountManagers.find(m => m.id === filterByManager)?.name
                      }
                      <button onClick={() => setFilterByManager('all')} className="ml-1 hover:text-emerald-900">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  
                  {filterByStatus !== 'all' && (
                    <Badge 
                      variant="secondary" 
                      className="flex items-center gap-1 bg-blue-50 text-blue-700"
                    >
                      {filterByStatus === 'active' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {filterByStatus === 'active' ? 'Actifs' : 'Inactifs'}
                      <button onClick={() => setFilterByStatus('all')} className="ml-1 hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  
                  {filterByDelivery !== 'all' && (
                    <Badge 
                      variant="secondary" 
                      className="flex items-center gap-1 bg-purple-50 text-purple-700"
                    >
                      <Package className="w-3 h-3" />
                      {filterByDelivery === 'delivered' ? 'Avec livraison' : 'Sans livraison'}
                      <button onClick={() => setFilterByDelivery('all')} className="ml-1 hover:text-purple-900">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  
                  {filterByProjects !== 'all' && (
                    <Badge 
                      variant="secondary" 
                      className="flex items-center gap-1 bg-orange-50 text-orange-700"
                    >
                      <Building2 className="w-3 h-3" />
                      {filterByProjects === 'with_projects' ? 'Avec projets' : 'Sans projet'}
                      <button onClick={() => setFilterByProjects('all')} className="ml-1 hover:text-orange-900">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  
                  <span className="text-sm text-slate-400 ml-2">
                    ({sortedClients.length} client{sortedClients.length > 1 ? 's' : ''})
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clients Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : sortedClients.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
              <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>
                {activeFiltersCount > 0 ? 'Aucun client ne correspond aux filtres' : 'Aucun client trouvé'}
              </h3>
              <p className="text-[#64748b]">
                {activeFiltersCount > 0 
                  ? 'Essayez de modifier vos critères de filtrage'
                  : 'Ajoutez votre premier client pour commencer'
                }
              </p>
              {activeFiltersCount > 0 && (
                <Button variant="outline" onClick={resetFilters} className="mt-4">
                  Réinitialiser les filtres
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="card overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">
                      <button 
                        onClick={() => toggleSort('name')}
                        className="flex items-center hover:text-slate-900"
                      >
                        Client
                        {getSortIcon('name')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700">Contact</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">
                      <button 
                        onClick={() => toggleSort('created_at')}
                        className="flex items-center justify-center hover:text-slate-900 w-full"
                      >
                        Création
                        {getSortIcon('created_at')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">
                      <button 
                        onClick={() => toggleSort('last_delivery')}
                        className="flex items-center justify-center hover:text-slate-900 w-full"
                      >
                        Dernière livraison
                        {getSortIcon('last_delivery')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700">
                      <button 
                        onClick={() => toggleSort('account_manager')}
                        className="flex items-center hover:text-slate-900"
                      >
                        Responsable
                        {getSortIcon('account_manager')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">
                      <button 
                        onClick={() => toggleSort('ca_year')}
                        className="flex items-center justify-end hover:text-slate-900 w-full"
                      >
                        CA {new Date().getFullYear()}
                        {getSortIcon('ca_year')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">
                      <button 
                        onClick={() => toggleSort('ca_total')}
                        className="flex items-center justify-end hover:text-slate-900 w-full"
                      >
                        CA Total
                        {getSortIcon('ca_total')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Projets</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-center">Statut</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedClients.map((client) => {
                    const revenue = clientsRevenue[client.id] || {};
                    return (
                      <TableRow 
                        key={client.id}
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => navigate(`/clients/${client.id}`)}
                        data-testid={`client-row-${client.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {/* Client Avatar */}
                            {client.avatar_url ? (
                              <img 
                                src={client.avatar_url.startsWith('/') 
                                  ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${client.avatar_url}` 
                                  : client.avatar_url
                                }
                                alt={`${client.first_name} ${client.last_name}`}
                                className="w-10 h-10 rounded-full object-cover border-2 border-slate-200"
                              />
                            ) : (
                              <Avatar 
                                name={`${client.first_name || ''} ${client.last_name || ''}`}
                                size="md"
                                type="client"
                              />
                            )}
                            <div>
                              <p className="font-medium text-slate-800">
                                {client.first_name} {client.last_name}
                              </p>
                              {client.company_name && (
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  {/* Company Logo */}
                                  {client.company_logo_url ? (
                                    <img 
                                      src={client.company_logo_url.startsWith('/') 
                                        ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${client.company_logo_url}` 
                                        : client.company_logo_url
                                      }
                                      alt={client.company_name}
                                      className="w-4 h-4 rounded object-contain"
                                    />
                                  ) : (
                                    <Building2 className="w-3 h-3 text-slate-400" />
                                  )}
                                  <span className="text-xs text-slate-500">{client.company_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {client.email && (
                              <a 
                                href={`mailto:${client.email}`} 
                                className="text-sm text-slate-600 hover:text-emerald-600 flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Mail className="w-3 h-3" />
                                {client.email}
                              </a>
                            )}
                            {client.phone && (
                              <a 
                                href={`tel:${client.phone}`} 
                                className="text-sm text-slate-500 hover:text-emerald-600 flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Phone className="w-3 h-3" />
                                {client.phone}
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm text-slate-600">
                            {formatDate(client.created_at)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center" data-testid={`last-delivery-${client.id}`}>
                          <span className={cn(
                            "text-sm",
                            client.last_delivery_date ? "text-slate-600" : "text-slate-400"
                          )}>
                            {client.last_delivery_date ? formatDate(client.last_delivery_date) : '-'}
                          </span>
                        </TableCell>
                        <TableCell data-testid={`account-manager-${client.id}`}>
                          {client.account_manager_name ? (
                            <div className="flex items-center gap-1.5">
                              <UserCircle className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-600">
                                {client.account_manager_name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {loadingRevenue ? (
                            <Loader2 className="w-4 h-4 animate-spin ml-auto" />
                          ) : (
                            <span className={cn(
                              "font-semibold",
                              revenue.ca_year > 0 ? "text-emerald-600" : "text-slate-400"
                            )}>
                              {formatCurrency(revenue.ca_year)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {loadingRevenue ? (
                            <Loader2 className="w-4 h-4 animate-spin ml-auto" />
                          ) : (
                            <span className={cn(
                              "font-bold",
                              revenue.ca_total > 0 ? "text-blue-600" : "text-slate-400"
                            )}>
                              {formatCurrency(revenue.ca_total)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="font-mono">
                            {revenue.projects_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            className={client.is_active !== false 
                              ? "bg-green-100 text-green-700" 
                              : "bg-gray-100 text-gray-700"
                            }
                          >
                            {client.is_active !== false ? 'Actif' : 'Inactif'}
                          </Badge>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
                                <Eye className="w-4 h-4 mr-2" /> Voir détails
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/projects?client=${client.id}`)}>
                                <Building2 className="w-4 h-4 mr-2" /> Voir projets
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600 focus:text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClient(client.id, `${client.first_name} ${client.last_name}`);
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}

        {/* Create Client Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouveau client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prénom</Label>
                  <Input
                    value={newClient.first_name}
                    onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                    placeholder="Jean"
                  />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input
                    value={newClient.last_name}
                    onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                    placeholder="Dupont"
                  />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="jean.dupont@example.com"
                  required
                />
              </div>
              <div>
                <Label>Société</Label>
                <Input
                  value={newClient.company_name}
                  onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                  placeholder="Nom de l'entreprise"
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="06 12 34 56 78"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Créer le client
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ClientsPage;
